import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { EmptyState } from "../components/EmptyState";
import {
  listAffiliateRedemptions,
  type AffiliateRedemptionRow,
  type AffiliateRedemptionStatusFilter,
} from "../api/affiliatePayouts";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";

// Affiliate-credit redemption queue.
//
// Bound to GET /v2/admin/bff/affiliate/redemptions. Operator-records
// flow only — the BE never moves on-chain funds for redemptions in
// this iteration. "Apply" records that the credit was honored for
// the requested Gao ecosystem target; "Reject" records refusal with
// a free-text reason. The user FE only creates queued rows.

function statusBadge(s: AffiliateRedemptionRow["status"]) {
  switch (s) {
    case "queued":
      return { label: "Queued", color: "var(--accent, #2c7be5)" };
    case "applied":
      return { label: "Applied", color: "#0a9444" };
    case "rejected":
      return { label: "Rejected", color: "#d23f3f" };
  }
}

function ActionLinks({ row }: { row: AffiliateRedemptionRow }) {
  const href = `/admin/affiliate/redemptions/${encodeURIComponent(row.id)}`;
  const styleBase = {
    fontSize: 11,
    padding: "2px 6px",
    border: "1px solid var(--border, #ccc)",
    borderRadius: 3,
    textDecoration: "none" as const,
    color: "var(--fg, inherit)",
  };
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      <Link to={href} style={styleBase}>
        View
      </Link>
      {row.status === "queued" && (
        <>
          <Link
            to={`${href}#apply`}
            style={{ ...styleBase, borderColor: "var(--accent, #2c7be5)", color: "var(--accent, #2c7be5)" }}
          >
            Apply
          </Link>
          <Link
            to={`${href}#reject`}
            style={{ ...styleBase, borderColor: "#d23f3f", color: "#d23f3f" }}
          >
            Reject
          </Link>
        </>
      )}
    </div>
  );
}

function AffiliateRedemptionsInner() {
  const [rows, setRows] = useState<AffiliateRedemptionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] =
    useState<AffiliateRedemptionStatusFilter>("queued");

  const load = useCallback(
    async (filter: AffiliateRedemptionStatusFilter) => {
      setError(null);
      setLoading(true);
      try {
        const r = await listAffiliateRedemptions({ status: filter });
        setRows(r.items);
      } catch (e) {
        setError(mapAffiliateError(e).message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(statusFilter);
  }, [load, statusFilter]);

  return (
    <div>
      <details style={{ margin: "8px 0" }}>
        <summary style={{ fontSize: 12, color: "var(--fg-dim)", cursor: "pointer" }}>
          Advanced — endpoint &amp; notes
        </summary>
        <p style={{ margin: "6px 0", fontSize: 12, color: "var(--fg-dim)" }}>
          Bound to <code>GET /v2/admin/bff/affiliate/redemptions</code> — bearer
          session + trusted device. Operator-records flow: Admin Web does NOT
          cross-call other Gao ecosystem services from the browser; Apply records
          intent for downstream processing.
        </p>
      </details>

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <label htmlFor="ar-status" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          Status:
        </label>
        <select
          id="ar-status"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as AffiliateRedemptionStatusFilter)
          }
          style={{ padding: "4px 8px", fontSize: 13 }}
        >
          <option value="queued">Queued</option>
          <option value="applied">Applied</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <button
          type="button"
          onClick={() => void load(statusFilter)}
          disabled={loading}
          style={{ padding: "4px 12px", fontSize: 13 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            border: "1px solid #d23f3f",
            background: "rgba(210, 63, 63, 0.06)",
            color: "#d23f3f",
            margin: "12px 0",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {rows && rows.length === 0 && (
        <EmptyState
          title={
            statusFilter === "queued"
              ? "No queued credit redemptions."
              : statusFilter === "all"
                ? "No credit redemptions yet."
                : `No ${statusFilter} credit redemptions.`
          }
          hint="Credit redemptions apply in-ecosystem credits (not cash). Review and apply or reject."
        />
      )}

      {rows && rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              minWidth: 1000,
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border, #ccc)", textAlign: "left" }}>
                <th style={{ padding: "8px 6px" }}>id</th>
                <th style={{ padding: "8px 6px" }}>identityId</th>
                <th style={{ padding: "8px 6px" }}>Amount</th>
                <th style={{ padding: "8px 6px" }}>Target</th>
                <th style={{ padding: "8px 6px" }}>Status</th>
                <th style={{ padding: "8px 6px" }}>Requested</th>
                <th style={{ padding: "8px 6px" }}>Applied</th>
                <th style={{ padding: "8px 6px" }}>Rejection</th>
                <th style={{ padding: "8px 6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = statusBadge(r.status);
                return (
                  <tr
                    key={r.id}
                    style={{ borderBottom: "1px solid var(--border-faint, #eee)" }}
                  >
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 12 }}>
                      <Link
                        to={`/admin/affiliate/redemptions/${encodeURIComponent(r.id)}`}
                        style={{ color: "var(--accent, #2c7be5)" }}
                      >
                        {r.id}
                      </Link>
                    </td>
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>
                      {r.identityId}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      <div style={{ fontFamily: "monospace" }}>
                        {r.amountHumanReadable ?? r.amountMinor}{" "}
                        <span style={{ color: "var(--fg-dim)" }}>{r.currency}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "var(--fg-dim)" }}>
                        {r.amountMinor} minor
                      </div>
                    </td>
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>
                      {r.target}
                    </td>
                    <td style={{ padding: "8px 6px", color: badge.color, fontWeight: 600 }}>
                      {badge.label}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      <code>{r.requestedAt}</code>
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      {r.appliedAt ? <code>{r.appliedAt}</code> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11, whiteSpace: "pre-wrap" }}>
                      {r.rejectionReason ?? "—"}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      <ActionLinks row={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AffiliateRedemptions() {
  return (
    <div>
      <PageHeader
        title="Affiliate redemptions"
        subtitle="Operator queue for affiliate-credit redemption against a Gao ecosystem target. Operator-records flow only — no on-chain side-effect."
        method="GET"
        path="/v2/admin/bff/affiliate/redemptions"
      />
      <AuthRequired>
        <AffiliateRedemptionsInner />
      </AuthRequired>
    </div>
  );
}
